import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Icon } from '@/components/Icon'
import { ApiError } from '@/lib/api'
import { formatNumber } from '@/lib/format'
import { fetchPage } from '@/lists/api'
import { CustomFieldsPanel } from './CustomFieldsPanel'
import { HistoryPanel } from './HistoryPanel'
import { ProductAttributes } from './ProductAttributes'
import { ProductEditSheet } from './ProductForm'
import { PhotoFrame, ProductGallery } from './ProductGallery'
import { fetchProduct, useProductOptions, type ProductCard } from './productApi'
import { useEffectiveTaxRates } from './taxRateApi'
import { Facts, Panel, RecordHeader, RecordTabs, StateView, type Fact, type MenuAction } from './record'
import { ProductPricesPanel } from '@/pricing/ProductPricesPanel'
import { CopyValue, listPath } from './recordHelpers'
import { FailureAlert, copyLinkItem, labelOf, numberOf, textOf } from './recordKit'
import type { RecordViewProps } from './types'

/**
 * Карточка товара (Product.dc.html): описание, характеристики, фото, остатки, история (V60).
 *
 * Поля товара читаются доменной карточкой (`GET /sales/products/{id}`), а не строкой списка:
 * у каталога списков нет описания, характеристик и фото, а правка идёт доменным PUT с
 * версией. Правка — боковой панелью целиком (`ProductEditSheet`), а не полем по колонке
 * через `bulk/update`: тот прошёл бы мимо правила «признаки учёта после движения не
 * меняются».
 *
 * Остатки — механизмом списков склада (та же область видимости, что в списке «Остатки»).
 * Свободно = количество − резерв: «лежит на полке» и «можно продать» — разные цифры.
 *
 * Описание — текст, а не разметка: абзацы по пустой строке, переводы строк сохраняются
 * (`white-space: pre-line`). React экранирует текст сам, поэтому `<b>` из описания выводится
 * буквами и не становится тегом — HTML-вставки быть не может по построению.
 *
 * ## Чего здесь нет, хотя макет это рисует
 *
 * - **Цены и прайс-листы, закупочная цена** — отдельная задача (прайс-листы).
 * - **Резервы по сделкам** и **ожидаемые поступления** — нет эндпоинтов; резерв виден в остатках.
 * - **Движения товара** — журнал движений отдельным списком не выведен.
 */
export function ProductRecordView({ recordId, record, context, notices }: RecordViewProps) {
  const { t, user, locale } = useApp()
  const queryClient = useQueryClient()
  const [tab, setTab] = useState('overview')
  const [failure, setFailure] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)

  const mayUpdate = can(user, 'sales.product', 'update')
  const card = useQuery({
    queryKey: ['product-card', recordId],
    queryFn: ({ signal }) => fetchProduct(recordId, signal),
    retry: false,
  })
  const options = useProductOptions()

  const mayReadStock = can(user, 'warehouse.stock', 'read')
  const stock = useQuery({
    queryKey: ['product-stock', recordId],
    enabled: mayReadStock,
    queryFn: ({ signal }) =>
      fetchPage(
        'warehouse.stock',
        {
          columns: ['warehouse_id', 'cell_id', 'quantity', 'reserved_quantity'],
          filters: [{ column: 'product_id', operator: 'eq', value: recordId }],
          sort: [],
          pageSize: 100,
        },
        null,
        signal,
      ),
    retry: false,
  })
  const taxRates = useEffectiveTaxRates()

  const values = record.values
  const product: ProductCard | undefined = card.data
  const name = product?.name ?? textOf(values, 'name') ?? textOf(values, 'sku') ?? recordId
  const sku = product?.sku ?? textOf(values, 'sku')
  const active = product ? product.active : values.is_active !== false
  const photos = product?.photos ?? []
  const mainPhoto = photos.find((photo) => photo.main) ?? null

  const rows = stock.data?.rows ?? []
  const quantity = rows.reduce((sum, row) => sum + (numberOf(row, 'quantity') ?? 0), 0)
  const reserved = rows.reduce((sum, row) => sum + (numberOf(row, 'reserved_quantity') ?? 0), 0)
  const free = quantity - reserved
  const minStock = product ? product.minStock ?? null : numberOf(values, 'min_stock')
  const taxCode = product?.taxRateCode ?? textOf(values, 'tax_rate_code')
  // The product stores a code; what it means today comes from the reference — 20 % in 2025,
  // 22 % from 2026 for the same "vat_standard".
  const taxRate = taxRates.data?.find((rate) => rate.code === taxCode && !rate.estimated)
  const below = minStock != null && stock.isSuccess && free < minStock
  const unit = product?.uomName ?? labelOf(values, 'uom_id')
  const number = (value: number | null | undefined, suffix?: string) =>
    value == null ? t('record.notSet') : `${formatNumber(value, locale)}${suffix ? ` ${suffix}` : ''}`

  const refreshList = () => void queryClient.invalidateQueries({ queryKey: ['list', 'sales.product'] })
  const patchCard = (patch: Partial<ProductCard>) =>
    queryClient.setQueryData<ProductCard>(['product-card', recordId], (current) => (current ? { ...current, ...patch } : current))

  const facts: Fact[] = []
  if (mayReadStock) {
    facts.push({
      id: 'free',
      label: t('record.product.fact.free'),
      value: stock.isSuccess ? formatNumber(free, locale) : '…',
      sub: stock.isSuccess ? (
        rows.length === 0 ? (
          <span className="flag flag-muted">{t('record.product.noStock')}</span>
        ) : below ? (
          <span className="flag flag-warning">
            <Icon name="alert" />
            {t('record.product.belowMin')}
          </span>
        ) : (
          t('record.product.fact.freeSub', { quantity: formatNumber(quantity, locale), reserved: formatNumber(reserved, locale) })
        )
      ) : undefined,
    })
  }
  facts.push(
    {
      id: 'vat',
      label: t('column.tax_rate_code'),
      value: taxCode == null ? t('record.notSet') : taxRate ? `${formatNumber(taxRate.percent, locale)} %` : taxCode,
      sub: taxRate ? taxRate.name : taxCode != null && taxRates.isSuccess ? t('record.product.taxNotEffective', { code: taxCode }) : undefined,
    },
    { id: 'unit', label: t('column.uom_id'), value: unit ?? t('record.notSet') },
    { id: 'weight', label: t('column.weight_kg'), value: number(product?.weightKg ?? numberOf(values, 'weight_kg')) },
    { id: 'min', label: t('column.min_stock'), value: minStock == null ? t('record.product.minNotSet') : formatNumber(minStock, locale) },
  )

  const menu: MenuAction[] = [copyLinkItem(t, setFailure)]

  const stockPanel = !mayReadStock ? (
    <StateView compact kind="forbidden" title={t('record.product.stock.forbidden')} />
  ) : stock.isLoading ? (
    <p className="muted">{t('app.loading')}</p>
  ) : stock.isError ? (
    <StateView
      compact
      kind={stock.error instanceof ApiError && stock.error.isForbidden ? 'forbidden' : 'error'}
      title={t('record.product.stock.error')}
    />
  ) : rows.length === 0 ? (
    <StateView compact kind="empty" title={t('record.product.stock.empty')} description={t('record.product.stock.emptyText')} />
  ) : (
    <div style={{ overflowX: 'auto' }}>
      <table className="tbl" aria-label={t('record.product.stock')}>
        <thead>
          <tr>
            <th>{t('column.warehouse_id')}</th>
            <th>{t('column.cell_id')}</th>
            <th className="num">{t('column.quantity')}</th>
            <th className="num">{t('column.reserved_quantity')}</th>
            <th className="num">{t('record.product.free')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const rowFree = (numberOf(row, 'quantity') ?? 0) - (numberOf(row, 'reserved_quantity') ?? 0)
            return (
              <tr key={row.id}>
                <td>{labelOf(row, 'warehouse_id') ?? '—'}</td>
                <td>
                  <span className="mono">{labelOf(row, 'cell_id') ?? '—'}</span>
                </td>
                <td className="num">{formatNumber(numberOf(row, 'quantity') ?? 0, locale)}</td>
                <td className="num">{formatNumber(numberOf(row, 'reserved_quantity') ?? 0, locale)}</td>
                <td className="num strong">{formatNumber(rowFree, locale)}</td>
              </tr>
            )
          })}
          <tr className="total">
            <td>{t('record.product.stockTotal')}</td>
            <td />
            <td className="num">{formatNumber(quantity, locale)}</td>
            <td className="num">{formatNumber(reserved, locale)}</td>
            <td className="num">{formatNumber(free, locale)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  )

  // Without the domain card nothing below can be shown truthfully; the stock still can.
  const cardState = card.isLoading ? (
    <p className="muted">{t('app.loading')}</p>
  ) : card.isError || !product ? (
    <StateView
      compact
      kind={card.error instanceof ApiError && card.error.isForbidden ? 'forbidden' : 'error'}
      title={t('product.card.error')}
    />
  ) : null

  const dimensions = product && (product.lengthMm != null || product.widthMm != null || product.heightMm != null)
    ? `${product.lengthMm ?? '—'} × ${product.widthMm ?? '—'} × ${product.heightMm ?? '—'} ${t('product.mm')}`
    : t('record.notSet')

  return (
    <div className="rec-page">
      <RecordHeader
        crumbs={[{ label: t('nav.products'), to: listPath('sales.product', '/products') }, { label: name }]}
        title={
          <span className="rec-title-row">
            <PhotoFrame photo={mainPhoto} compact />
            {name}
            {product && <span className="badge">{t(`value.sales.product.product_type.${product.productType}`)}</span>}
            <span className={active ? 'badge b-success' : 'badge'}>
              <Icon name={active ? 'check' : 'close'} />
              {active ? t('record.product.active') : t('record.product.inactive')}
            </span>
            {product?.marked && <span className="badge b-info">{t('product.badge.marked')}</span>}
          </span>
        }
        meta={[
          sku ? (
            <span key="sku" className="row" style={{ gap: 4 }}>
              <span className="muted">{t('column.sku')}</span>
              <CopyValue value={sku} label={t('record.copyNumber', { number: sku })} />
            </span>
          ) : null,
          product?.barcode ? (
            <span key="ean" className="row" style={{ gap: 6 }}>
              <span className="muted">{t('column.barcode')}</span>
              <span className="mono">{product.barcode}</span>
            </span>
          ) : null,
          product?.categoryTrail && product.categoryTrail.length > 0 ? (
            <span key="cat">{product.categoryTrail.join(' / ')}</span>
          ) : (
            labelOf(values, 'category_id')
          ),
        ]}
        primary={mayUpdate && product ? { id: 'edit', label: t('product.edit.open'), icon: 'edit', onClick: () => setEditing(true) } : null}
        menu={menu}
        menuLabel={t('record.more')}
      />
      <FailureAlert text={failure} />
      {notices}
      {below && (
        <div className="alert alert-warning" role="status">
          <Icon name="alert" />
          <span className="fg">
            {t('record.product.belowMinAlert', { free: formatNumber(free, locale), min: formatNumber(minStock ?? 0, locale) })}
          </span>
        </div>
      )}
      <Facts items={facts} ariaLabel={t('record.facts')} />

      <RecordTabs
        tabs={[
          { id: 'overview', label: t('product.tab.overview') },
          { id: 'photos', label: t('product.tab.photos'), count: product ? photos.length : null },
          { id: 'stock', label: t('record.product.stock'), count: mayReadStock && stock.isSuccess ? rows.length : null },
          { id: 'prices', label: t('product.tab.prices') },
          { id: 'fields', label: t('card.tab.customFields') },
          { id: 'history', label: t('record.tab.history') },
        ]}
        active={tab}
        onChange={setTab}
        ariaLabel={t('record.product.sections')}
      >
        {tab === 'overview' && (
          <div className="layout-2-wide" style={{ paddingTop: 14 }}>
            <div className="rec-col">
              <Panel title={t('product.form.section.description')}>
                {cardState ?? (product?.description ? (
                  <Description text={product.description} />
                ) : (
                  <StateView compact kind="empty" title={t('product.description.empty')}
                             description={mayUpdate ? t('product.description.emptyText') : undefined} />
                ))}
              </Panel>
              <Panel title={t('product.attr.title')} count={product?.attributes?.length}>
                {cardState ?? (product && (
                  <ProductAttributes
                    productId={recordId}
                    attributes={product.attributes ?? []}
                    mayEdit={mayUpdate}
                    onSaved={(attributes) => patchCard({ attributes })}
                  />
                ))}
              </Panel>
            </div>
            <div className="rec-col">
              <Panel title={t('product.tab.photos')}>
                {cardState ?? (
                  <button type="button" className="pg-hero" onClick={() => setTab('photos')}
                          aria-label={t('product.photo.openGallery')}>
                    <PhotoFrame photo={mainPhoto} large />
                  </button>
                )}
              </Panel>
              <Panel title={t('record.product.details')}>
                {cardState ?? (product && (
                  <dl className="pa-list">
                    <Detail label={t('column.category_id')}>
                      {product.categoryTrail && product.categoryTrail.length > 0 ? product.categoryTrail.join(' / ') : t('record.notSet')}
                    </Detail>
                    <Detail label={t('product.form.tracking')}>
                      {t(`product.tracking.${product.batchTracked ? 'batch' : product.serialTracked ? 'serial' : 'none'}`)}
                    </Detail>
                    <Detail label={t('column.is_marked')}>{product.marked ? t('adm.on') : t('adm.off')}</Detail>
                    <Detail label={t('column.storage_condition')}>
                      {t(`value.sales.product.storage_condition.${product.storageCondition}`)}
                    </Detail>
                    <Detail label={t('product.form.shelfLifeDays')}>{number(product.shelfLifeDays)}</Detail>
                    <Detail label={t('product.form.reorderPoint')}>{number(product.reorderPoint)}</Detail>
                    <Detail label={t('product.form.volumeM3')}>{number(product.volumeM3)}</Detail>
                    <Detail label={t('product.dimensions')}>{dimensions}</Detail>
                  </dl>
                ))}
                {product?.accountingLocked && (
                  <p className="t-sm muted" style={{ marginTop: 8 }}>
                    <Icon name="lock" size="sm" /> {t('product.form.lockedNote')}
                  </p>
                )}
              </Panel>
            </div>
          </div>
        )}
        {tab === 'photos' && (
          <div className="rec-col" style={{ paddingTop: 14 }}>
            <Panel title={t('product.tab.photos')} count={product ? photos.length : undefined}>
              {cardState ?? (
                <ProductGallery
                  productId={recordId}
                  photos={photos}
                  mayEdit={mayUpdate}
                  maxPhotoBytes={options.data?.maxPhotoBytes}
                  maxPhotos={options.data?.maxPhotos}
                  onChanged={(next) => {
                    if (next) patchCard({ photos: next })
                    else void card.refetch()
                  }}
                />
              )}
            </Panel>
          </div>
        )}
        {tab === 'prices' && (
          <div className="rec-col" style={{ paddingTop: 14 }}>
            <ProductPricesPanel productId={recordId} />
          </div>
        )}
        {tab === 'stock' && (
          <div className="rec-col" style={{ paddingTop: 14 }}>
            <Panel title={t('record.product.stock')} flush>
              {stockPanel}
            </Panel>
          </div>
        )}
        {tab === 'fields' && (
          <div className="rec-col" style={{ paddingTop: 14 }}>
            <Panel title={t('card.tab.customFields')}>
              <CustomFieldsPanel entity={context.entity} recordId={recordId} resource={context.resource} />
            </Panel>
          </div>
        )}
        {tab === 'history' && (
          <div className="rec-col" style={{ paddingTop: 14 }}>
            <Panel title={t('record.history.changes')}>
              <HistoryPanel entity={context.entity} recordId={recordId} />
            </Panel>
          </div>
        )}
      </RecordTabs>

      {editing && product && (
        <ProductEditSheet
          card={product}
          onClose={() => setEditing(false)}
          onSaved={(saved) => {
            queryClient.setQueryData(['product-card', recordId], saved)
            refreshList()
            setEditing(false)
          }}
        />
      )}
    </div>
  )
}

/** Описание абзацами: пустая строка — граница абзаца, одиночный перевод строки сохраняется. */
export function Description({ text }: { text: string }) {
  const paragraphs = text.split(/\n\s*\n/).map((part) => part.trim()).filter((part) => part !== '')
  return (
    <div className="pd-text">
      {paragraphs.map((paragraph, index) => (
        <p key={index}>{paragraph}</p>
      ))}
    </div>
  )
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="pa-row">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  )
}
